import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useNotificationWebSocket } from '../hooks/useNotificationWebSocket';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { NotificationOut } from '../types/api';
import { apiService } from '../services/api';

interface NewOrderEvent {
  orderId: number;
  eventId: number;
  receivedAt: number;
}

interface OrderUpdateEvent {
  orderId: number;
  receivedAt: number;
}

interface StockUpdatedEvent {
  localId: number;
  productId: number;
  receivedAt: number;
}

interface NotificationContextType {
  notifications: NotificationOut[];
  unreadCount: number;
  connected: boolean;
  /** Increments on every WebSocket (re)connection: refetch what may have been missed. */
  connectionId: number;
  error: string | null;
  isLoading: boolean;
  newOrderEvent: NewOrderEvent | null;
  /** Last real-time notification about a specific order (status change, etc.). */
  lastOrderUpdate: OrderUpdateEvent | null;
  lastStockUpdate: StockUpdatedEvent | null;
  pushNotifications: {
    isSupported: boolean;
    isRegistered: boolean;
    permission: NotificationPermission;
    register: () => Promise<void>;
    unregister: () => Promise<void>;
  };
  markAsRead: (notificationId: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: number) => Promise<void>;
  clearAll: () => Promise<void>;
  refreshNotifications: () => Promise<void>;
  requestUnreadCount: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: ReactNode;
  orderId?: number; // For anonymous order notifications
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ 
  children, 
  orderId 
}) => {
  const { user, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState<NotificationOut[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [newOrderEvent, setNewOrderEvent] = useState<NewOrderEvent | null>(null);
  const [lastOrderUpdate, setLastOrderUpdate] = useState<OrderUpdateEvent | null>(null);
  const [lastStockUpdate, setLastStockUpdate] = useState<StockUpdatedEvent | null>(null);
  const isLoadingRef = useRef(false);

  // Push notifications for PWA (works when app is closed)
  // VAPID key is fetched from backend automatically
  // Enable for authenticated users OR anonymous users with orderId
  const pushNotifications = usePushNotifications({
    enabled: isAuthenticated || !!orderId,
    orderId: !isAuthenticated && orderId ? orderId : undefined,
  });

  // Memoize notification handler to prevent unnecessary re-renders
  const handleNotification = useCallback((notification: NotificationOut) => {
    if (notification.order_id) {
      setLastOrderUpdate({ orderId: notification.order_id, receivedAt: Date.now() });
    }
    // Add notification to list if not already present
    setNotifications((prev) => {
      const exists = prev.some((n) => n.id === notification.id);
      if (exists) {
        return prev;
      }
      return [notification, ...prev];
    });
  }, []);

  const handleUnreadCountChange = useCallback(() => {
    // Unread count is managed by WebSocket
  }, []);

  const handleNewOrder = useCallback((data: { order_id: number; event_id: number }) => {
    setNewOrderEvent({ orderId: data.order_id, eventId: data.event_id, receivedAt: Date.now() });
  }, []);

  const handleStockUpdated = useCallback((data: { local_id: number; product_id: number }) => {
    setLastStockUpdate({ localId: data.local_id, productId: data.product_id, receivedAt: Date.now() });
  }, []);

  // WebSocket connection for real-time notifications
  // For authenticated users, use userId. For anonymous orders, use orderId.
  const {
    notifications: wsNotifications,
    unreadCount: wsUnreadCount,
    connected,
    connectionId,
    error,
    requestUnreadCount,
  } = useNotificationWebSocket({
    userId: isAuthenticated ? user?.id : undefined,
    orderId: !isAuthenticated && orderId ? orderId : undefined,
    enabled: isAuthenticated || !!orderId,
    onNotification: handleNotification,
    onUnreadCountChange: handleUnreadCountChange,
    onNewOrder: handleNewOrder,
    onStockUpdated: handleStockUpdated,
  });

  /**
   * Load initial notifications from API
   */
  const loadNotifications = useCallback(async () => {
    if (!isAuthenticated && !orderId) {
      return;
    }

    // Prevent duplicate concurrent calls (e.g., from React StrictMode)
    if (isLoadingRef.current) {
      return;
    }

    try {
      isLoadingRef.current = true;
      setIsLoading(true);
      // For anonymous users, pass order_id. For authenticated users, JWT token is used automatically
      const response = await apiService.getNotifications({
        limit: 50,
        offset: 0,
        order_id: !isAuthenticated && orderId ? orderId : undefined,
      });
      
      setNotifications(response.notifications || []);
      setInitialLoadDone(true);
    } catch (err) {
      console.error('❌ Error loading notifications:', err);
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  }, [isAuthenticated, orderId]);

  /**
   * Mark notification as read
   */
  const markAsRead = useCallback(async (notificationId: number) => {
    if (!isAuthenticated) {
      // For anonymous orders, just update local state
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
      );
      return;
    }

    try {
      await apiService.markNotificationAsRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
      );
    } catch (err) {
      console.error('❌ Error marking notification as read:', err);
      throw err;
    }
  }, [isAuthenticated]);

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async () => {
    if (!isAuthenticated) {
      // For anonymous orders, just update local state
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      return;
    }

    try {
      await apiService.markAllNotificationsAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (err) {
      console.error('❌ Error marking all notifications as read:', err);
      throw err;
    }
  }, [isAuthenticated]);

  /**
   * Delete notification
   */
  const deleteNotification = useCallback(async (notificationId: number) => {
    if (!isAuthenticated) {
      // For anonymous orders, just update local state
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      return;
    }

    try {
      await apiService.deleteNotification(notificationId);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
    } catch (err) {
      console.error('❌ Error deleting notification:', err);
      throw err;
    }
  }, [isAuthenticated]);

  /**
   * Clear all notifications
   */
  const clearAll = useCallback(async () => {
    if (!isAuthenticated) {
      // For anonymous orders, just clear local state
      setNotifications([]);
      return;
    }

    try {
      await apiService.clearAllNotifications();
      setNotifications([]);
    } catch (err) {
      console.error('❌ Error clearing all notifications:', err);
      throw err;
    }
  }, [isAuthenticated]);

  /**
   * Refresh notifications from API
   */
  const refreshNotifications = useCallback(async () => {
    await loadNotifications();
  }, [loadNotifications]);

  // Load initial notifications when authenticated or orderId changes
  useEffect(() => {
    if ((isAuthenticated || orderId) && !initialLoadDone) {
      loadNotifications();
    }
  }, [isAuthenticated, orderId, initialLoadDone, loadNotifications]);

  // Merge WebSocket notifications with API notifications
  useEffect(() => {
    if (wsNotifications.length > 0) {
      setNotifications((prev) => {
        const merged = [...prev];
        wsNotifications.forEach((wsNotif) => {
          const index = merged.findIndex((n) => n.id === wsNotif.id);
          if (index === -1) {
            merged.unshift(wsNotif);
          } else {
            merged[index] = wsNotif;
          }
        });
        return merged;
      });
    }
  }, [wsNotifications]);

  // Calculate unread count from notifications
  const unreadCount = notifications.filter((n) => !n.read).length;

  // Auto-register for push notifications when authenticated OR when anonymous user has orderId
  useEffect(() => {
    if (
      (isAuthenticated || orderId) &&
      pushNotifications.isSupported && 
      !pushNotifications.isRegistered && 
      pushNotifications.permission === 'granted'
    ) {
      pushNotifications.register();
    }
  }, [isAuthenticated, orderId, pushNotifications.isSupported, pushNotifications.isRegistered, pushNotifications.permission, pushNotifications.register]);

  const value: NotificationContextType = {
    notifications,
    unreadCount: wsUnreadCount > 0 ? wsUnreadCount : unreadCount,
    connected,
    connectionId,
    error,
    isLoading,
    newOrderEvent,
    lastOrderUpdate,
    lastStockUpdate,
    pushNotifications: {
      isSupported: pushNotifications.isSupported,
      isRegistered: pushNotifications.isRegistered,
      permission: pushNotifications.permission,
      register: pushNotifications.register,
      unregister: pushNotifications.unregister,
    },
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll,
    refreshNotifications,
    requestUnreadCount,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
